import { useState } from "react";
import { X } from "lucide-react";
import type { CreditCard } from "../../lib/types";

interface Props {
  card: CreditCard;
  onClose: () => void;
  onPay: (cardId: string, amount: number, note: string) => Promise<void>;
}

const fmt = (n: number) =>
  n.toLocaleString("es-MX", { maximumFractionDigits: 0 });

export function PaymentModal({ card, onClose, onPay }: Props) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const quickAmounts = [
    { label: "Minimo", value: card.minimum_payment },
    { label: "$1,000", value: 1000 },
    { label: "$2,000", value: 2000 },
    { label: "$5,000", value: 5000 },
  ];

  const handleSubmit = async () => {
    const val = parseFloat(amount);
    if (!val || val <= 0) return;
    setLoading(true);
    try {
      await onPay(card.id, val, note);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
      <div className="card p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-100">
            Registrar Pago
          </h3>
          <button onClick={onClose} className="btn-ghost p-1">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="mb-4 p-3 rounded-lg bg-surface-2">
          <div className="flex items-center gap-2 mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: card.color }}
            />
            <span className="text-sm font-medium text-gray-200">
              {card.name}
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Saldo actual: ${fmt(card.balance)} | Disponible: $
            {fmt(card.available)}
          </p>
        </div>

        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block">Monto</label>
          <input
            type="number"
            className="input w-full"
            placeholder="0.00"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            autoFocus
          />
        </div>

        <div className="flex gap-2 mb-4 flex-wrap">
          {quickAmounts.map((q) => (
            <button
              key={q.label}
              className="btn-ghost text-xs"
              onClick={() => setAmount(String(q.value))}
            >
              {q.label} (${fmt(q.value)})
            </button>
          ))}
        </div>

        <div className="mb-4">
          <label className="text-xs text-gray-500 mb-1 block">
            Nota (opcional)
          </label>
          <input
            type="text"
            className="input w-full"
            placeholder="Ej: Pago quincenal"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !amount || parseFloat(amount) <= 0}
            className="btn-primary disabled:opacity-50"
          >
            {loading ? "Registrando..." : "Registrar Pago"}
          </button>
        </div>
      </div>
    </div>
  );
}
